/* import __COLOCATED_TEMPLATE__ from './playground-heading.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import FinPlaygroundExport from 'embercom/lib/fin-playground-test-export';
import type { Playground } from 'embercom/lib/fin-playground/playground';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type CsvService from 'embercom/services/csv';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type { GroupSummary } from 'embercom/lib/fin-playground/group-summary';

interface PlaygroundHeadingArgs {
  canUsePlaygroundGroups: boolean;
  isPlaygroundLocked: boolean;
  showResetModal: boolean;
  updateShowConfirmTestRunModal: (showConfirmTestRunModal: boolean) => void;
  showExceedingQuestionLimitBanner: boolean;
  updateShowExceedingQuestionLimitBanner: (showExceedingQuestionLimitBanner: boolean) => void;
  updateShowTestSettingsModal: (showTestSettingsModal: boolean) => void;
  updateShowResetModal: (showResetModal: boolean) => void;
  addQuestionDropdownOptions: Array<{
    items: Array<{
      text: string;
      value?: string;
      icon?: string;
      isDisabled?: boolean;
      onSelectItem?: () => void;
    }>;
  }>;
  selectAddQuestionDropdownOption: (option: string) => void;
  shouldUseTestSettingsModal: boolean;
  handlePlaygroundSettingsChanged: () => void;
  isDetailsModuleOpen: boolean;
  playgroundIsLoading: boolean;
  playground: Playground;
  teammateHasNoAccessToAllConversations: boolean;
  groupSummaries?: GroupSummary[];
  openNewGroupModal: (place: string) => void;
  manageGroupsDropdownOptions?: Array<{
    items: Array<{
      text: string;
      value: string;
      onSelectItem: () => void;
    }>;
  }>;
}

const SURVEY_ID = 45346448;

export default class PlaygroundHeading extends Component<PlaygroundHeadingArgs> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare csv: CsvService;
  @service declare intercomEventService: $TSFixMe;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  get isAddQuestionDropdownDisabled() {
    return (
      this.args.isPlaygroundLocked ||
      this.args.playgroundIsLoading ||
      this.args.playground.hasReachedQuestionLimit
    );
  }

  get runTestButtonLabel(): string {
    return this.args.playground.isRunning
      ? this.intl.t('ai-agent.playground.running-test')
      : this.intl.t('ai-agent.playground.run-test');
  }

  get deductionRequiredToAddQuestion(): number {
    if (!this.args.playground.hasReachedQuestionLimit) {
      return 0;
    }

    return 1 + this.args.playground.questions.length - this.args.playground.questionLimit;
  }

  get groupSummaryItems() {
    if (!this.args.groupSummaries) {
      return;
    }

    return [
      {
        items: this.args.groupSummaries.map((groupSummary) => ({
          text: groupSummary.name,
          value: groupSummary.id,
        })),
      },
    ];
  }

  get footerActions() {
    return [
      {
        text: this.intl.t('ai-agent.playground.header-buttons.create-new-test'),
        icon: 'new',
        action: () => this.args.openNewGroupModal('playground_header'),
      },
    ];
  }

  @action onSelectItem(groupId: number) {
    if (groupId === this.args.playground.id) {
      return;
    } else {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'group_dropdown_item',
        context: 'playground',
      });

      this.router.transitionTo(this.router.currentRouteName, groupId);
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }

  @action
  downloadResults() {
    let { data, fileName } = new FinPlaygroundExport(
      this.appService.app,
      this.intl,
      this.args.playground,
      this.router,
      this.aiContentSegmentsService.segments,
    );

    this.csv.export(data, {
      fileName,
      withSeparator: false,
    });

    this.intercomEventService.trackAnalyticsEvent({
      action: 'downloaded_results',
      object: 'playground',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::PlaygroundHeading': typeof PlaygroundHeading;
    'ai-agent/playground/playground-heading': typeof PlaygroundHeading;
  }
}
